import { ExternalVendorKeyValue, WorkyCustomField, CorpType, EmailAddress, PhoneNumber } from "../../common/CommonInterfaces";
import { UserType } from "../../remoteExecution/RemoteExecutionServiceImpl";

interface AgencyTeamMemberDTO {
  readonly userEmail: EmailAddress;
  readonly firstName: string;
  readonly lastName: string;
  readonly userPhoneNumber: PhoneNumber;
  readonly agencyUserRole: UserType;
  readonly agencyName: string;
  readonly sellerClientId: string;
}

interface AgencyTeamMembersResponseDTO {
  readonly teamMembers: AgencyTeamMemberDTO[];
}

interface AssignTeamMemberReqDTO {
  readonly leadIds: readonly string[]; // Max 100 records
  /** When set as null, it means the lead is un-assigned */
  readonly assignToUserEmail: string | null;
}

enum AgencySalesLeadStatus {
  NEW = "NEW",
  CONTACTED = "CONTACTED",
  INTERESTED = "INTERESTED",
  FOLLOW_UP_REQUIRED = "FOLLOW_UP_REQUIRED",
  // NOT_INTERESTED = "NOT_INTERESTED",
  CLOSED = "CLOSED",
  NOT_QUALIFIED = "NOT_QUALIFIED",
  FUTURE_CONTACT = "FUTURE_CONTACT",
  NO_PICK_UP = "NO_PICK_UP",
  DEMO_BOOKED = "DEMO_BOOKED",
  /**
     * This is a very special status,
     *
     *
     * It enables the sellerClient to based on a re-occurring schedule
     * track against NEW intent signals for this lead
     *
     * Make sure the user is aware of the special significant of this status
     */
  SMART_WATCH = "SMART_WATCH",
}

enum SalesLeadMeetingType {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  IN_PERSON_ON_LEAD_SITE = "IN_PERSON_ON_LEAD_SITE",
  IN_PERSON_IN_OUR_OFFICE = "IN_PERSON_IN_OUR_OFFICE",
  IN_PERSON_LUNCH = "IN_PERSON_LUNCH",
  IN_PERSON_OTHER = "IN_PERSON_OTHER",
  VIDEO_CALL = "VIDEO_CALL",
  OTHER = "OTHER",
}

enum LeadEventType {
  LEAD_CREATION = "LEAD_CREATION",
  STATUS_CHANGE = "STATUS_CHANGE",
  NOTE_CREATION = "NOTE_CREATION",
  NOTE_UPDATE = "NOTE_UPDATE",
  OTHER = "OTHER",
}

enum WorkyTaskType {
  CALENDAR_REMINDER = "CALENDAR_REMINDER",
}





interface FieldValueDTO {
  valueDetails: ExternalVendorKeyValue;
  createdDateTimeEpochSecEST: number;
  updateDateTimeEpochSecEST: number | null;
}

interface IntentSignalSubFieldValueDTO{
  intentSignalFieldNameAndId: WorkyCustomField;
  fieldValues: FieldValueDTO[]
}

interface IntentSignalFieldValuesDTO {
  intentSignalFieldName: WorkyCustomField;
  intentSignalSubFieldValues: IntentSignalSubFieldValueDTO[];
}

interface IntentSignalAttributesDTO {
  intentKey: WorkyCustomField;
  intentSignalFieldValues: IntentSignalFieldValuesDTO[];
}

interface IntentSignalDetailsDTO {
  intentSignalAttributes: IntentSignalAttributesDTO[];
}

interface UserDetailsDTO {
  readonly userEmail: EmailAddress;
  readonly userPhoneNumber: PhoneNumber;
  readonly lastName: string;
  readonly firstName: string;
  readonly userId: string;
  readonly createdDateTime: string | null;
}

interface WorkyCorpObjDTO {
  readonly corpBackendName: string;
  readonly corpType: CorpType;
  readonly defaultSellerClientId: string | null;
  readonly corpDisplayName: string;
}

interface WorkyLeadsSellerClientUserDetailsDTO {
  readonly userDetails: UserDetailsDTO;
  readonly userType: UserType;
  readonly corpObj: WorkyCorpObjDTO;
  readonly profilePicUrl: string | null;
}

interface LeadEventRecordDTO {
  readonly leadEventRecordId: string;
  readonly backendAutomaticallyCreatedEventNote: string;
  readonly userCreatedEventNote: string | null;
  readonly eventType: LeadEventType;
  readonly leadEventEventRecordUserDetails: WorkyLeadsSellerClientUserDetailsDTO;
  readonly referenceKeys: Record<string, string>;
  readonly createdDateTimeEpochSec: number;
  readonly updatedDateTimeEpochSec: number | null;
}

interface ContactDetailsDTO {
  workyContactUUID: string;
  salesLeadContactEmailAddress?: string[];
  salesLeadContactPhoneNumber?: string[];
  socialMediaProfileLinks?: string[];
  contactName?: string;
  contactJobTitle?: string;
  contactPhotoUrl?: string;
  salesLeadContactEmailAddressImproved?: ExternalVendorKeyValue[];
  salesLeadContactPhoneNumberImproved?: ExternalVendorKeyValue[];
}

// Get type containing all property names as string literals
type ContactDetailKeys = keyof ContactDetailsDTO;

// Create a constant object with all the key names
const CONTACT_DETAIL_KEYS = {
  EMAIL: 'salesLeadContactEmailAddress',
  PHONE: 'salesLeadContactPhoneNumber',
  SOCIAL: 'socialMediaProfileLinks',
  NAME: 'contactName',
  JOB_TITLE: 'contactJobTitle'
} as const;

// Type-safe property access
function getContactProperty<K extends keyof ContactDetailsDTO>(
  contact: ContactDetailsDTO, 
  key: K
): ContactDetailsDTO[K] {
  return contact[key];
}

interface BusinessDetailsDTO {
  businessDescription: string;
  businessYearsOfOperation: string;
  lineOfBusiness?: string[];
  competitors?:string[];
  regionsOfOperation?:string[];
  numberOfEmployees: number;
  socialMediaLinks?: ExternalVendorKeyValue[];
  revenueEstimate?: string;
  potentialSizeOfBusiness: string;
}

interface AgencySalesLeadNoteDTO {
  noteUUID: string;
  note: string;
  meetingType: SalesLeadMeetingType;
  contactDateTimeEpochSec: number;
  noteAuthorFirstName: string;
  noteAuthorLastName: string;
  noteAuthorEmail: string;
  noteAuthorPhone: string;
  businessContactName?: string;
  businessContactDetails?: ContactDetailsDTO;
  shouldFollowUp: boolean;
  noteAttachments?: readonly SalesLeadNoteAttachmentDTO[];
}

interface SalesLeadNoteAttachmentDTO {
  readonly attachmentId: string;
  readonly storageKey: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly uploadedAtEpochSec?: number | null;
  readonly declaredSizeBytes?: number | null;
  readonly status: SalesLeadNoteAttachmentStatus;
  readonly uploaderPrimaryWorkyUserId: string;
  readonly uploaderUserEmail: string;
}

enum SalesLeadNoteAttachmentStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

interface AssignedUserDetailsDTO {
  readonly userEmail: string;
  readonly userPhone: string;
  readonly firstName: string;
  readonly lastName: string;
}

interface AssignedUserObjDTO{
  /** Email address of the user who requested the assignment */
  readonly requestUserEmailAddress: EmailAddress;
  /** Details of the assigned user (null means un-assigned) */
  readonly assignedUserDetails: AssignedUserDetailsDTO | null;
  /** When the assignment was made (epoch seconds) */
  readonly assignmentDateTimeEpochSec: number;
}

interface AgencySalesLeadDTO {
  sellerBusinessName: string; // agencyName
  businessName: string;
  businessCity: string;
  parentLeadSpecId?: string;
  businessAddress: string;
  salesLeadUUID: string;
  leadFirstGenerationDateTimeEpochSec: number;
  website: string;
  socialMediaLinks?: string[];
  sourceOfLead: string;  
  contactDetails: ContactDetailsDTO[];
  businessDetails: BusinessDetailsDTO;
  agencySalesLeadStatus: AgencySalesLeadStatus;
  lastContactedDateTimeEpochSec: number;
  agencySalesLeadNotes: AgencySalesLeadNoteDTO[];  
  businessIconURL?: string;
  intentSignalDetails?: IntentSignalDetailsDTO;
  leadEventRecords?: LeadEventRecordDTO[];
  /** Optional: direct field for current assignee's email (null or missing means unassigned) */
  assignedToUserEmail?: string | null;
  /** Full assignment history or current record */
  assignedUsers: readonly AssignedUserObjDTO[];
  tags: readonly TagDTO[];
  /** Company phone numbers from external vendors (key-value per vendor) */
  companyPhoneNumbers: readonly ExternalVendorKeyValue[];
}
interface EmailCalendarTaskSmallDTO{
  readonly epochDateTimeSecondsESTForCalendar:number;
  readonly browserRequestURL: string;
}

interface CreateAgencySalesLeadNoteDTO {
  agencySalesLeadUUID: string;
  note: string;
  meetingType?: SalesLeadMeetingType; // Default: SalesLeadMeetingType.OTHER
  businessContactName?: string;
  businessContactDetails?: ContactDetailsDTO;
  contactJobTitle?: string,
  shouldFollowUp?: boolean;
  newLeadStatus?: AgencySalesLeadStatus;
  emailCalendarTaskDTO?: EmailCalendarTaskSmallDTO;
  noteAttachments?: readonly SalesLeadNoteAttachmentDTO[];
}
interface UpdateSalesLeadNoteDTO{
  agencySalesLeadUUID: string;
  agencySalesLeadNoteUUID: string;
  note: string;
  noteAttachments?: readonly SalesLeadNoteAttachmentDTO[];
}

interface UpdateSalesLeadStatusDTO {
  leadId: string;
  status: AgencySalesLeadStatus;
}

enum BulkUpdateStatusType {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

interface BulkUpdateStatusResultItemDTO {
  readonly leadId: string;
  readonly status: BulkUpdateStatusType;
}

interface BulkUpdateSalesLeadStatusResponseDTO {
  readonly results: readonly BulkUpdateStatusResultItemDTO[];
}

interface BulkUpdateSalesLeadStatusDTO {
  readonly leadIds: readonly string[];
  readonly status: AgencySalesLeadStatus;
}

interface CreateSalesLeadRequestDTO {
  businessNameForSalesLead: string;
  businessCity?: string;
  socialMediaLinks?: string[];
  businessIconURL?: string;
  businessAddress?: string;
  website?: string;
  sourceOfLead?: string;
  contactDetails?: ContactDetailsDTO[];
  agencySalesLeadNotes?: AgencySalesLeadNoteDTO[]; // Default is empty array in Java
  businessDetails?: BusinessDetailsDTO;
  customDetailsForLead?: Record<string, string>;
}

enum AgencySalesLeadSizeOfBusiness {
  UNKNOWN = "UNKNOWN",
  SOLE_PROPRIETOR = "SOLE_PROPRIETOR",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
  ENTERPRISE = "ENTERPRISE",
}

interface CreateTargetBusinessLeadOrgEmployeeContactRequestDTO{
  targetBusinessLeadOrgWorkyId: string;
  parentSellerClientLeadSpecIds: string[];
  contactDetails: ContactDetailsDTO;
}

interface CreateTargetBusinessLeadOrgEmployeeContactResponseDTO{
  workyLeadUserId: string;
  contactDetails: ContactDetailsDTO;
  success: boolean;
  message: string;
}

enum LeadElementFeedbackValue {
  /**
   * int -100, false, fully wrong
   */
  WRONG = "WRONG",
  /**
   * int 0, initial status
   */
  INITIAL_STATUS = "INITIAL_STATUS",
  /**
   * int 100, correct
   */
  CORRECT = "CORRECT"
}

// Helper function to get accuracy score for enum values
function getLeadElementFeedbackAccuracyScore(value: LeadElementFeedbackValue): number {
  switch (value) {
    case LeadElementFeedbackValue.WRONG:
      return -100;
    case LeadElementFeedbackValue.INITIAL_STATUS:
      return 0;
    case LeadElementFeedbackValue.CORRECT:
      return 100;
    default:
      return 0;
  }
}

// Helper function to get name for enum values
function getLeadElementFeedbackName(value: LeadElementFeedbackValue): string {
  switch (value) {
    case LeadElementFeedbackValue.WRONG:
      return "Wrong";
    case LeadElementFeedbackValue.INITIAL_STATUS:
      return "Initial Status"; 
    case LeadElementFeedbackValue.CORRECT:
      return "Correct";
    default:
      return "Unknown";
  }
}

interface MarkContactElementValueReqDTO {
  /**
   * This is the contacts actual contact element value
   * E.g their existing email address, their existing phone number, etc.
   */
  salesLeadContactExistingElements: string;
  /**
   * This is the worky contact id of the contact who's contact element value is being marked
   * the worky uuid for the contact
   */
  workyContactId: string;
  /**
   * LeadElementFeedbackValue.accuracyScore
   * needs to be a number of the accuracy score of the marked response
   * -100 for wrong, 0 for initial status, 100 for correct
   */
  markedResponse: string;
}

interface MarkContactElementValueResDTO {
  success: boolean;
  message: string;
}

interface UpdateContactDetailReqDTO {
  /**
   * The value of the contact element (e.g., email, phone number, etc.)
   */
  contactElementValue: string;
  /**
   * The name of the data source for this contact detail
   */
  dataSourceName?: string;
  /**
   * The unique Worky contact detail ID
   */
  workyContactDetailId: string;
}

interface UpdateContactDetailResultDTO{
  workyContactDetailId: string;
  success: boolean;
  message: string;
}

interface UpdateContactDetailResponseDTO {
  results: UpdateContactDetailResultDTO[];
}

interface SendEmailRequestDTO {
  readonly emailHeader: string;
  readonly emailBody: string;
  readonly toEmails: EmailAddress[];
}

interface SendEmailResponseDTO {
  readonly success: boolean;
  readonly message: string;
}

interface CurrentLeadsAPIRespObj {
  readonly leads: readonly AgencySalesLeadDTO[];
  readonly exclusiveStartKey: string | null;
}

const FIRST_CALL_EXCLUSIVE_START_KEY: string = 'FIRST_CALL_EXCLUSIVE_START_KEY';
interface TagDTO {
  readonly tagId: string;
  readonly tagName: string;
  readonly tagDescription?: string;
}

interface WorkyLeadTagDTO {
  readonly tagName: string;
  readonly tagDescription?: string;
}

interface SellerClientTagDTO {
  readonly tagId: string;
  readonly tagName: string;
  readonly tagDescription?: string;
  readonly sellerClientId: string;
}

interface TagDTO {
  readonly tagId: string;
  readonly tagName: string;
  readonly tagDescription?: string;
}

interface AssignedTagsToLeadDTO {
  readonly leadId: string;
  readonly sellerClientId: string;
  readonly tags: TagDTO[];
}

interface AssignTagToLeadDTO {
  readonly tagId: string;
  readonly leadId: string;
}

interface AssignTagToLeadsDTO {
  readonly tagId: string;
  readonly leadIds: readonly string[];
}

interface BatchTagAssignmentResult {
  readonly success: boolean;
  readonly successfulLeadIds: readonly string[];
  readonly message?: string;
}

interface LeadStatusChangeEventDTO {
  readonly userEmail: string;
  readonly leadId: string;
  readonly oldStatus: string;
  readonly newStatus: string;
  readonly leadName: string;
  readonly leadStatusChangeEventEpochSeconds: number;
}

interface GroupedLeadStatusChangeEventsDTO {
  readonly sellerClientId: string;
  readonly totalCallsMade: readonly LeadStatusChangeEventDTO[];
  readonly closed: readonly LeadStatusChangeEventDTO[];
  readonly interested: readonly LeadStatusChangeEventDTO[];
  readonly deadLeads: readonly LeadStatusChangeEventDTO[];
  readonly contactedLeads: readonly LeadStatusChangeEventDTO[];
}

enum EmailCalendarTaskDetailRespStatus {
  /**
   * When request has been successfully sent via email server
   */
  SUCCESS = "SUCCESS",
  /**
   * When request is recieved by Worky backend, and request is saved to DDB
   */
  PENDING = "PENDING",
  FAILURE = "FAILURE",
}

interface EmailCalendarTaskDetailRespDTO {
  readonly deliveryEmailAddress: string;
  readonly status: EmailCalendarTaskDetailRespStatus;
  readonly errorMessage?: string;
}

interface EmailCalendarTaskRespDTO {
  readonly emailCalendarTaskDetails: readonly EmailCalendarTaskDetailRespDTO[];
}

interface EmailCalendarTaskDTO {
  readonly toEmailAddresses: readonly string[];
  readonly emailHeader: string;
  readonly messageNote: string;
  readonly epochDateTimeSecondsESTForCalendar: number;
  readonly simpleTargetLeadSellerClientView?: SimpleTargetLeadSellerClientViewDTO;
  browserRequestURL: string;
}

interface SimpleTargetLeadSellerClientViewDTO {
  readonly targetLeadId: string;
  readonly parentLeadSellerClientId: string;
  readonly targetLeadName: string;  
}

interface EmailCalendarTaskEventDTO {
  readonly requestId: string;
  readonly sellerClientId: string;
  readonly emailHeader?: string;
  readonly messageNote?: string;
  readonly epochDateTimeSecondsESTForCalendar: number;
  readonly generalErrorMessage?: string;
  readonly createdDateTimeEpochSeconds: number;
  readonly toEmailAddresses?: readonly string[];
  readonly simpleTargetLeadSellerClientViewDTO?: SimpleTargetLeadSellerClientViewDTO;
}

interface ViewEventsTeamMemberDTO {
  readonly teamMemberEmail: EmailAddress;
  readonly events: readonly EmailCalendarTaskEventDTO[];
}

interface ViewEventsDayDTO {
  readonly dateString: string;
  readonly dateEpochSeconds: number;
  readonly eventsByTeamMember: readonly ViewEventsTeamMemberDTO[];
}

interface ViewEventsRespDTO {
  readonly eventsByDay: readonly ViewEventsDayDTO[];
}

interface CreateContactDetailDTO {
  readonly contactName: string;
  readonly contactEmail?: string;
  readonly contactPhoneNumber?: string;
  readonly contactJobTitle?: string;
}

interface CreateSalesLeadRequestV2DTO {
  readonly businessName: string;
  readonly businessAddress?: string;
  readonly businessDescription?: string;
  readonly note?: string;
  readonly contactDetails?: readonly CreateContactDetailDTO[];
}

export {
  AgencyTeamMemberDTO,
  AgencyTeamMembersResponseDTO,
  AssignTeamMemberReqDTO,
  AssignedUserObjDTO,
  AssignedUserDetailsDTO,
  AgencySalesLeadSizeOfBusiness,
  CreateSalesLeadRequestDTO,
  CreateAgencySalesLeadNoteDTO,
  ContactDetailsDTO,
  BusinessDetailsDTO,
  SalesLeadMeetingType,
  AgencySalesLeadStatus,
  AgencySalesLeadNoteDTO,
  SalesLeadNoteAttachmentDTO,
  SalesLeadNoteAttachmentStatus,
  AgencySalesLeadDTO,
  UpdateSalesLeadStatusDTO,
  UpdateSalesLeadNoteDTO,
  CreateTargetBusinessLeadOrgEmployeeContactRequestDTO,
  CreateTargetBusinessLeadOrgEmployeeContactResponseDTO,
  LeadElementFeedbackValue,
  getLeadElementFeedbackAccuracyScore,
  getLeadElementFeedbackName,
  MarkContactElementValueReqDTO,
  MarkContactElementValueResDTO,
  IntentSignalDetailsDTO,
  IntentSignalAttributesDTO,
  IntentSignalFieldValuesDTO,
  LeadEventType,
  UserDetailsDTO,
  WorkyCorpObjDTO,
  WorkyLeadsSellerClientUserDetailsDTO,
  LeadEventRecordDTO,
  UpdateContactDetailReqDTO,
  UpdateContactDetailResultDTO,
  UpdateContactDetailResponseDTO,
  SendEmailRequestDTO,
  SendEmailResponseDTO,
  CurrentLeadsAPIRespObj,
  FIRST_CALL_EXCLUSIVE_START_KEY,
  SellerClientTagDTO,
  TagDTO,
  AssignedTagsToLeadDTO,
  AssignTagToLeadDTO,
  AssignTagToLeadsDTO,
  BatchTagAssignmentResult,
  WorkyLeadTagDTO,
  LeadStatusChangeEventDTO,
  GroupedLeadStatusChangeEventsDTO,
  BulkUpdateStatusType,
  BulkUpdateStatusResultItemDTO,
  BulkUpdateSalesLeadStatusResponseDTO,
  BulkUpdateSalesLeadStatusDTO,
  WorkyTaskType,
  EmailCalendarTaskDetailRespStatus,
  EmailCalendarTaskDetailRespDTO,
  EmailCalendarTaskRespDTO,
  EmailCalendarTaskDTO,
  EmailCalendarTaskSmallDTO,
  EmailCalendarTaskEventDTO,
  SimpleTargetLeadSellerClientViewDTO,
  ViewEventsTeamMemberDTO,
  ViewEventsDayDTO,
  ViewEventsRespDTO,
  CreateContactDetailDTO,
  CreateSalesLeadRequestV2DTO,
};
